import { useState, useEffect } from 'react';
import { assetPath } from './images';
import {
  companyInfo as fallbackCompanyInfo,
  reviews as fallbackReviews,
  projects as fallbackProjects,
  curatedShowcaseProjects as fallbackShowcase,
  mainServices as fallbackServices,
} from '../data/siteData';

// Helper to fetch content from the public CMS endpoint
export async function getCmsList(collectionName) {
  try {
    const res = await fetch(`/_api/public/content/${collectionName}`);
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data) ? data : null;
  } catch (err) {
    console.warn(`Could not load CMS collection "${collectionName}":`, err);
    return null;
  }
}

// Hook to fetch and provide editable company info
export function useCompanyInfo() {
  const [info, setInfo] = useState(fallbackCompanyInfo);

  useEffect(() => {
    let isMounted = true;
    getCmsList('virksomhedsoplysninger').then((items) => {
      if (isMounted && items && items.length > 0) {
        const item = items[0];
        setInfo({
          ...fallbackCompanyInfo,
          name: item.firmanavn || fallbackCompanyInfo.name,
          shortName: item.kort_navn || fallbackCompanyInfo.shortName,
          founder: item.grundlaegger || fallbackCompanyInfo.founder,
          address: item.adresse || fallbackCompanyInfo.address,
          phone: item.telefon || fallbackCompanyInfo.phone,
          phoneFormatted: item.telefon ? item.telefon.replace(/\s+/g, '') : fallbackCompanyInfo.phoneFormatted,
          email: item.email || fallbackCompanyInfo.email,
          cvr: item.cvr || fallbackCompanyInfo.cvr,
          facebook: item.facebook || fallbackCompanyInfo.facebook,
          slogan: item.slogan || fallbackCompanyInfo.slogan,
          logo: item.logo?.[0] ? assetPath(item.logo[0]) : fallbackCompanyInfo.logo,
          logoBlack: item.logo?.[0] ? assetPath(item.logo[0]) : fallbackCompanyInfo.logoBlack,
          logoLarge: item.logo?.[0] ? assetPath(item.logo[0]) : fallbackCompanyInfo.logoLarge,
          heroVideo: item.hero_video?.[0] ? assetPath(item.hero_video[0]) : fallbackCompanyInfo.heroVideo,
          heroPoster: item.hero_billede?.[0] ? assetPath(item.hero_billede[0]) : fallbackCompanyInfo.heroPoster,
        });
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return info;
}

// Hook to fetch and provide customer reviews
export function useReviews() {
  const [reviews, setReviews] = useState(fallbackReviews);

  useEffect(() => {
    let isMounted = true;
    getCmsList('anmeldelser').then((items) => {
      if (isMounted && items && items.length > 0) {
        const parsed = items.map((item) => {
          // Parse HTML text or string
          const rawText = item.tekst || '';
          const cleanText = rawText.replace(/<\/?[^>]+(>|$)/g, '').trim();
          return {
            author: item.forfatter || '',
            title: item.overskrift || '',
            text: cleanText,
            htmlText: rawText,
            rating: item.terms?.['anmeldelse-stjerner']?.[0]?.startsWith('4') ? 4 : 5,
          };
        });
        setReviews(parsed);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return reviews;
}

// Hook to fetch and provide projects
export function useProjects() {
  const [projectsList, setProjectsList] = useState(fallbackProjects);

  useEffect(() => {
    let isMounted = true;
    getCmsList('projekter').then((items) => {
      if (isMounted && items && items.length > 0) {
        const mapped = items.map((item) => {
          const categorySlug = item.terms?.['projekt-kategori']?.[0] || 'privat';
          const category = categorySlug === 'erhverv' ? 'Erhverv' : 'Privat';
          const slugVal = typeof item.slug === 'object' ? item.slug?.da : item.slug;

          // Parse specs from rich text or HTML list
          const specsHtml = item.specifikationer || '';
          const specsMatches = [...specsHtml.matchAll(/<li>(.*?)<\/li>/gi)].map(m => m[1]);
          const specs = specsMatches.length > 0 ? specsMatches : [];

          // Clean description
          const rawDesc = item.beskrivelse || '';
          const cleanDesc = rawDesc.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p><p>/gi, '\n\n').replace(/<\/?[^>]+(>|$)/g, '').trim();

          return {
            slug: slugVal,
            path: `/nyheder/${slugVal.replace(/^nyheder-/, '')}`,
            title: item.titel || '',
            category,
            date: item.dato || '',
            mainImage: item.hovedbillede?.[0] || '',
            gallery: item.galleri?.length > 0 ? item.galleri : (item.hovedbillede || []),
            specs,
            description: cleanDesc,
            metaTitle: item.meta_titel || item.titel,
            metaDesc: item.meta_beskrivelse || cleanDesc.slice(0, 160),
          };
        });
        setProjectsList(mapped);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return projectsList;
}

// Hook to fetch and provide curated showcase projects
export function useShowcaseProjects() {
  const [showcase, setShowcase] = useState(fallbackShowcase);

  useEffect(() => {
    let isMounted = true;
    getCmsList('udvalgte_referencer').then((items) => {
      if (isMounted && items && items.length > 0) {
        const sorted = [...items].sort((a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99));
        setShowcase(sorted.map((item) => ({
          title: item.titel || '',
          category: item.kategori_tekst || '',
          image: item.billede?.[0] || '',
          desc: item.beskrivelse || '',
          link: item.link || '',
        })));
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return showcase;
}

// Hook to fetch and provide services
export function useServices() {
  const [services, setServices] = useState(fallbackServices);

  useEffect(() => {
    let isMounted = true;
    getCmsList('ydelser').then((items) => {
      if (isMounted && items && items.length > 0) {
        const sorted = [...items].sort((a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99));
        // Merge with existing sublinks so navigation and full details remain intact
        const merged = sorted.map((item) => {
          const existing = fallbackServices.find(s => s.href === item.link || s.title.toLowerCase() === (item.titel || '').toLowerCase());
          return {
            title: item.titel || existing?.title || '',
            href: item.link || existing?.href || '',
            image: item.billede?.[0] || existing?.image || '',
            desc: item.beskrivelse || existing?.desc || '',
            sublinks: existing?.sublinks || [],
          };
        });
        setServices(merged);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return services;
}

// Hook to fetch page-specific content (hero text, badge, SEO)
export function usePageContent(slug, defaultValues = {}) {
  const [content, setContent] = useState(defaultValues);

  useEffect(() => {
    let isMounted = true;
    getCmsList('sider').then((items) => {
      if (isMounted && items && items.length > 0) {
        const match = items.find((item) => {
          const itemSlug = typeof item.slug === 'object' ? item.slug?.da : item.slug;
          return itemSlug === slug || (item.titel && item.titel.toLowerCase() === slug.toLowerCase());
        });
        if (match) {
          setContent({
            badge: match.hero_badge || defaultValues.badge,
            heading: match.hero_overskrift || defaultValues.heading,
            subheading: match.hero_underoverskrift || defaultValues.subheading,
            textHtml: match.hero_tekst || defaultValues.textHtml,
            text: match.hero_tekst ? match.hero_tekst.replace(/<\/?[^>]+(>|$)/g, '').trim() : defaultValues.text,
            image: match.hero_billede?.[0] || defaultValues.image,
            metaTitle: match.meta_titel || defaultValues.metaTitle,
            metaDescription: match.meta_beskrivelse || defaultValues.metaDescription,
          });
        }
      }
    });
    return () => {
      isMounted = false;
    };
  }, [slug]);

  return content;
}
